import React, { useMemo, useEffect, useState, memo, KeyboardEvent } from 'react';
import { AnalyticsService } from '@box-core/services';
import { useInjector } from '@box-react/hooks/use-injector';
import { GlobalStateService } from '@box-core/services/global-state.service';
import { translate as t } from '@box-core/services/language.service';
import { Router } from '@angular/router';
import { addressToHeaderText, timeslotToText, getAddressIcon } from '@box/utils';
import { BoxHeaderDeliveryDetailsProps } from './box-header-delivery-details.types';
import { AngularWrapper } from '@box-react/components/angular-wrapper/angular-wrapper';
import { SvgIconComponent } from '@box-shared/components';
import { MatIcon } from '@angular/material/icon';
import { Address } from '@box-types';

function BoxHeaderDeliveryDetailsComponent({
  address,
  timeslot,
  onAddressChange,
  onTimeslotChange
}: BoxHeaderDeliveryDetailsProps) {
  const [location, setLocation] = useState(null);

  const currentUrl = useInjector().get(Router)?.url;
  const analyticsService = useInjector().get(AnalyticsService);
  const globalStateService = useInjector().get(GlobalStateService);

  const addressText = useMemo(() => {
    if (location && !address) {
      return addressToHeaderText({ city: location.name } as Address, t);
    } else {
      return addressToHeaderText(address, t);
    }
  }, [address, location]);
  const timeslotText = useMemo(() => timeslotToText(timeslot), [timeslot]);
  const addressIconPath = useMemo(() => getAddressIcon(address), [address]);

  useEffect(() => {
    const subscription = globalStateService.location$.subscribe((location) => {
      if (!location?.name) return;
      setLocation(location);
    });
    return () => subscription?.unsubscribe();
  }, []);

  const handleAddressClick = () => {
    analyticsService.addGACustomEvent('address_change', { source: currentUrl });
    onAddressChange?.();
  };

  const handleTimeslotClick = () => {
    onTimeslotChange?.();
  };

  return (
    <div className="header-delivery-details-component" role="group" aria-label="Delivery options">
      <div
        className="delivery-details-button address"
        onClick={handleAddressClick}
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleAddressClick();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Change delivery address"
      >
        {addressIconPath && (
          <AngularWrapper
            component={SvgIconComponent}
            className="delivery-details-button-address-icon"
            inputs={{ svgPath: addressIconPath }}
          ></AngularWrapper>
        )}
        <span className="details-text ods-typography-body-s-bold">{t(addressText)}</span>
        <div className="delivery-details-button-arrow">
          <AngularWrapper component={MatIcon}>keyboard_arrow_down</AngularWrapper>
        </div>
      </div>

      <div
        className="delivery-details-button schedule"
        onClick={handleTimeslotClick}
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTimeslotClick();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Change delivery schedule"
      >
        <div className="delivery-details-button-timeslot-icon">
          <AngularWrapper component={MatIcon}>schedule</AngularWrapper>
        </div>
        <span className="details-text ods-typography-body-s-bold">{t(timeslotText)}</span>
        <div className="delivery-details-button-arrow">
          <AngularWrapper component={MatIcon}>keyboard_arrow_down</AngularWrapper>
        </div>
      </div>
    </div>
  );
}

export default memo(BoxHeaderDeliveryDetailsComponent);
